import React, { useState, useEffect } from 'react';
import { BookOpen, FileText, Download, Eye, Menu, X, Sun, Moon, BarChart3, User, LogOut, TrendingUp } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart } from 'recharts';

// Complete syllabus data with full modules
const syllabusData = {
  Mathematics: {
    Mains: [
      { topic: "Sets, Relations and Functions", modules: ["Sets and their representation", "Union, intersection, complement", "Venn diagrams", "Relations - reflexive, symmetric, transitive", "Functions - one-one, onto, into", "Composition and inverse functions"] },
      { topic: "Complex Numbers", modules: ["Complex numbers as ordered pairs", "Argand diagram", "Modulus and argument", "De Moivre's theorem", "Quadratic equations in complex system", "Roots of unity"] },
      { topic: "Matrices and Determinants", modules: ["Types of matrices", "Matrix operations", "Transpose, symmetric matrices", "Determinants and properties", "Adjoint and inverse", "System of linear equations", "Cramer's rule"] },
      { topic: "Permutations and Combinations", modules: ["Fundamental principle of counting", "Permutations with/without repetition", "Circular permutations", "Combinations", "Binomial coefficients"] },
      { topic: "Binomial Theorem", modules: ["Binomial theorem for positive integers", "Pascal's triangle", "General and middle terms", "Properties of binomial coefficients"] },
      { topic: "Sequences and Series", modules: ["AP, GP, HP", "Arithmetic and geometric means", "Sum of n terms", "Infinite GP", "Special series"] },
      { topic: "Limits and Continuity", modules: ["Limits and evaluation", "L'Hospital's rule", "Continuity at a point", "Types of discontinuities", "Intermediate value theorem"] },
      { topic: "Differentiation", modules: ["Derivatives of functions", "Chain rule", "Implicit differentiation", "Logarithmic differentiation", "Parametric forms", "Second order derivatives"] },
      { topic: "Applications of Derivatives", modules: ["Rate of change", "Tangents and normals", "Increasing/decreasing functions", "Maxima and minima", "Rolle's and Lagrange's theorems"] },
      { topic: "Integration", modules: ["Indefinite integrals", "Integration by substitution", "Integration by parts", "Partial fractions", "Definite integrals", "Area under curves"] },
      { topic: "Differential Equations", modules: ["Order and degree", "Formation of DEs", "Variable separable", "Homogeneous equations", "Linear differential equations"] },
      { topic: "Coordinate Geometry 2D", modules: ["Straight lines", "Circles", "Parabola", "Ellipse", "Hyperbola", "Tangent and normal"] },
      { topic: "3D Geometry", modules: ["Direction cosines", "Equation of line in space", "Skew lines", "Equation of plane", "Angle between planes"] },
      { topic: "Vectors", modules: ["Vector operations", "Dot and cross product", "Scalar triple product", "Vector triple product"] },
      { topic: "Statistics and Probability", modules: ["Measures of dispersion", "Probability axioms", "Conditional probability", "Bayes' theorem", "Binomial distribution"] },
      { topic: "Trigonometry", modules: ["Trigonometric identities", "Inverse trigonometric functions", "Properties of triangles", "Heights and distances"] }
    ],
    Advanced: [
      { topic: "Algebra", modules: ["Complex numbers advanced", "Quadratic equations", "Sequences and series", "Matrices and determinants", "Probability theory"] },
      { topic: "Calculus", modules: ["Limits and continuity", "Differentiation techniques", "Applications of derivatives", "Integration methods", "Definite integrals", "Differential equations"] },
      { topic: "Vectors", modules: ["Vector algebra", "Dot and cross products", "Triple products", "Applications in geometry"] },
      { topic: "Coordinate Geometry", modules: ["2D analytical geometry", "Conic sections advanced", "3D geometry", "Planes and lines in space"] }
    ]
  },
  Physics: {
    Mains: [
      { topic: "Kinematics", modules: ["Motion in straight line", "Motion in plane", "Projectile motion", "Uniform circular motion", "Relative velocity"] },
      { topic: "Laws of Motion", modules: ["Newton's laws", "Friction", "Circular motion dynamics", "Impulse and momentum"] },
      { topic: "Work, Energy and Power", modules: ["Work by constant/variable force", "Kinetic and potential energy", "Conservation of energy", "Collisions"] },
      { topic: "Rotation", modules: ["Centre of mass", "Torque", "Angular momentum", "Moment of inertia", "Rolling motion"] },
      { topic: "Gravitation", modules: ["Universal law", "Acceleration due to gravity", "Orbital velocity", "Escape velocity", "Satellites"] },
      { topic: "Thermodynamics", modules: ["Heat and temperature", "Laws of thermodynamics", "Carnot engine", "Entropy"] },
      { topic: "Electrostatics", modules: ["Coulomb's law", "Electric field and potential", "Gauss's law", "Capacitance"] },
      { topic: "Current Electricity", modules: ["Ohm's law", "Kirchhoff's laws", "RC circuits", "Wheatstone bridge"] },
      { topic: "Magnetism", modules: ["Magnetic field", "Biot-Savart law", "Ampere's law", "Magnetic materials"] },
      { topic: "EMI", modules: ["Faraday's law", "Lenz's law", "Self and mutual inductance", "AC circuits"] },
      { topic: "Optics", modules: ["Reflection and refraction", "Lenses and mirrors", "Wave optics", "Interference and diffraction"] },
      { topic: "Modern Physics", modules: ["Photoelectric effect", "Bohr model", "Radioactivity", "Nuclear physics"] }
    ],
    Advanced: [
      { topic: "Mechanics", modules: ["Kinematics 2D/3D", "Newton's laws applications", "Work-energy theorem", "Rotational dynamics", "Gravitation"] },
      { topic: "Thermal Physics", modules: ["Thermodynamic processes", "Kinetic theory", "Heat transfer", "Calorimetry"] },
      { topic: "Electricity & Magnetism", modules: ["Electrostatics advanced", "Current electricity", "Magnetic effects", "Electromagnetic induction"] },
      { topic: "Optics & Modern Physics", modules: ["Wave optics", "Geometrical optics", "Quantum mechanics basics", "Nuclear physics"] }
    ]
  },
  Chemistry: {
    Mains: [
      { topic: "Atomic Structure", modules: ["Subatomic particles", "Bohr model", "Quantum numbers", "Electronic configuration", "Periodic trends"] },
      { topic: "Chemical Bonding", modules: ["Ionic and covalent bonds", "VSEPR theory", "Hybridization", "Molecular orbital theory", "Hydrogen bonding"] },
      { topic: "States of Matter", modules: ["Gas laws", "Kinetic theory", "Liquid state", "Solid state", "Crystal lattices"] },
      { topic: "Thermodynamics", modules: ["First law", "Enthalpy", "Hess's law", "Entropy", "Gibbs energy"] },
      { topic: "Equilibrium", modules: ["Chemical equilibrium", "Le Chatelier's principle", "Ionic equilibrium", "pH and buffers", "Solubility product"] },
      { topic: "Redox & Electrochemistry", modules: ["Oxidation numbers", "Electrochemical cells", "Nernst equation", "Electrolysis", "Corrosion"] },
      { topic: "Chemical Kinetics", modules: ["Rate of reaction", "Order and molecularity", "Integrated rate laws", "Arrhenius equation"] },
      { topic: "Organic Chemistry Basics", modules: ["Nomenclature", "Isomerism", "Electronic effects", "Reaction mechanisms", "Organic reactions"] },
      { topic: "Hydrocarbons", modules: ["Alkanes", "Alkenes", "Alkynes", "Aromatic compounds", "Reactions"] },
      { topic: "Functional Groups", modules: ["Alcohols and phenols", "Ethers", "Aldehydes and ketones", "Carboxylic acids", "Amines"] },
      { topic: "Biomolecules", modules: ["Carbohydrates", "Proteins", "Amino acids", "Nucleic acids", "Vitamins"] }
    ],
    Advanced: [
      { topic: "Physical Chemistry", modules: ["Atomic structure advanced", "Chemical bonding theories", "Thermodynamics", "Chemical kinetics", "Electrochemistry"] },
      { topic: "Inorganic Chemistry", modules: ["Periodic properties", "s-block elements", "p-block elements", "d-block elements", "Coordination compounds"] },
      { topic: "Organic Chemistry", modules: ["Reaction mechanisms", "Named reactions", "Stereochemistry", "Organic synthesis", "Spectroscopy"] }
    ]
  },
  "General Aptitude": {
    Mains: [
      { topic: "Numerical Ability", modules: ["Number systems", "Percentages", "Profit & loss", "Ratio & proportion", "Time & work", "Data interpretation"] },
      { topic: "Logical Reasoning", modules: ["Series completion", "Coding-decoding", "Blood relations", "Seating arrangements", "Puzzles", "Syllogisms"] },
      { topic: "Verbal Ability", modules: ["Reading comprehension", "Grammar", "Vocabulary", "Sentence correction", "Para jumbles"] }
    ],
    Advanced: [
      { topic: "Advanced Reasoning", modules: ["Pattern recognition", "Analytical reasoning", "Critical thinking", "Data analysis", "Problem solving"] }
    ]
  }
};

const generatePapers = () => {
  const papers = [];
  const subjects = ["Mathematics", "Physics", "Chemistry"];
  for (let year = 1975; year <= 2024; year++) {
    subjects.forEach(subject => {
      if (year >= 2013) {
        papers.push({ id: `m-${subject}-${year}`, subject, year, exam: "JEE Mains", title: `${subject} - JEE Mains ${year}` });
      }
      papers.push({ id: `a-${subject}-${year}`, subject, year, exam: year >= 2013 ? "JEE Advanced" : "IIT-JEE", title: `${subject} - ${year >= 2013 ? "JEE Advanced" : "IIT-JEE"} ${year}` });
    });
  }
  return papers.reverse();
};

const historicalData = [
  { year: 1975, mainsQualified: 8500, advancedQualified: 2100, iitAdmissions: 1800, syllabusChanges: 0 },
  { year: 1980, mainsQualified: 12000, advancedQualified: 3200, iitAdmissions: 2400, syllabusChanges: 1 },
  { year: 1985, mainsQualified: 18000, advancedQualified: 4500, iitAdmissions: 3200, syllabusChanges: 0 },
  { year: 1990, mainsQualified: 25000, advancedQualified: 6000, iitAdmissions: 4100, syllabusChanges: 2 },
  { year: 1995, mainsQualified: 35000, advancedQualified: 8500, iitAdmissions: 5200, syllabusChanges: 1 },
  { year: 2000, mainsQualified: 55000, advancedQualified: 12000, iitAdmissions: 6500, syllabusChanges: 3 },
  { year: 2005, mainsQualified: 75000, advancedQualified: 15000, iitAdmissions: 7800, syllabusChanges: 2 },
  { year: 2010, mainsQualified: 125000, advancedQualified: 18500, iitAdmissions: 9500, syllabusChanges: 1 },
  { year: 2013, mainsQualified: 150000, advancedQualified: 150000, iitAdmissions: 10500, syllabusChanges: 5 },
  { year: 2015, mainsQualified: 178000, advancedQualified: 178000, iitAdmissions: 11000, syllabusChanges: 2 },
  { year: 2018, mainsQualified: 245000, advancedQualified: 245000, iitAdmissions: 11300, syllabusChanges: 3 },
  { year: 2020, mainsQualified: 250000, advancedQualified: 250000, iitAdmissions: 16000, syllabusChanges: 1 },
  { year: 2022, mainsQualified: 268000, advancedQualified: 268000, iitAdmissions: 16598, syllabusChanges: 2 },
  { year: 2024, mainsQualified: 280000, advancedQualified: 280000, iitAdmissions: 17000, syllabusChanges: 1 }
];

export default function IITJEECatalogue() {
  const [auth, setAuth] = useState(false);
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState('syllabus');
  const [subject, setSubject] = useState('Mathematics');
  const [examType, setExamType] = useState('Mains');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [dark, setDark] = useState(false);
  const [paper, setPaper] = useState(null);
  const [papers] = useState(generatePapers());

  const subjects = ["Mathematics", "Physics", "Chemistry", "General Aptitude"];
  const filtered = papers.filter(p => p.subject === subject && p.exam.includes(examType));

  const bg = dark ? '#0f0f1e' : '#f5f7fa';
  const card = dark ? '#1a1a2e' : '#ffffff';
  const text = dark ? '#e4e4e7' : '#333333';
  const muted = dark ? '#a1a1aa' : '#666666';
  const border = dark ? '#2d2d44' : '#e0e0e0';
  const primary = dark ? '#4dabf7' : '#667eea';

  if (!auth) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <div style={{ textAlign: 'center', padding: '3rem', background: 'white', borderRadius: '20px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', maxWidth: '500px' }}>
          <BookOpen size={80} color="#667eea" style={{ marginBottom: '1.5rem' }} />
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>IITJEE Learning Catalogue</h1>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', color: '#666' }}>Complete prep for JEE Mains & Advanced</p>
          <button onClick={() => { setAuth(true); setUser({ name: "JEE Aspirant", email: "student@example.com" }); }} style={{ background: '#667eea', color: 'white', border: 'none', padding: '1rem 2.5rem', fontSize: '1.1rem', borderRadius: '50px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            <User size={20} /> Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100vh',marginTop:'85px', display: 'flex', flexDirection: 'column', background: bg, color: text, fontFamily: 'system-ui, -apple-system, sans-serif', overflow: 'hidden' }}>
      <nav style={{ background: card, borderBottom: `1px solid ${border}`, padding: '0.75rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => setMobileMenu(!mobileMenu)} style={{ background: 'none', border: 'none', color: text, cursor: 'pointer', padding: '0.5rem', display: window.innerWidth < 768 ? 'block' : 'none' }}>
            <Menu size={24} />
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <BookOpen size={28} color={primary} />
            <span style={{ fontSize: '1.3rem', fontWeight: '600' }}>IITJEE Catalogue</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {[{ id: 'syllabus', icon: BookOpen, label: 'Syllabus' }, { id: 'papers', icon: FileText, label: 'Papers' }, { id: 'analytics', icon: BarChart3, label: 'Analytics' }].map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ background: tab === t.id ? primary : 'transparent', color: tab === t.id ? '#fff' : text, border: tab === t.id ? 'none' : `1px solid ${border}`, padding: '0.6rem 1.2rem', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', fontWeight: '500' }}>
                <t.icon size={18} /> <span>{t.label}</span>
              </button>
            ))}
          </div>
          <button onClick={() => setDark(!dark)} style={{ background: dark ? '#2d2d44' : '#f0f0f0', border: 'none', padding: '0.6rem', borderRadius: '8px', cursor: 'pointer', color: text }}>
            {dark ? <Sun size={20} /> : <Moon size={20} />}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: dark ? '#2d2d44' : '#f0f0f0', padding: '0.5rem 1rem', borderRadius: '8px' }}>
            <User size={20} />
            <span>{user.name}</span>
          </div>
        </div>
      </nav>

      {mobileMenu && (
        <div onClick={() => setMobileMenu(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 999 }}>
          <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: 0, left: 0, bottom: 0, width: '280px', background: card, boxShadow: '2px 0 10px rgba(0,0,0,0.3)', overflowY: 'auto', zIndex: 1000 }}>
            <div style={{ padding: '1rem', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between' }}>
              <h5 style={{ margin: 0 }}>Subjects</h5>
              <button onClick={() => setMobileMenu(false)} style={{ background: 'none', border: 'none', color: text, cursor: 'pointer' }}><X size={24} /></button>
            </div>
            {subjects.map(s => (
              <div key={s} onClick={() => { setSubject(s); setMobileMenu(false); }} style={{ padding: '1rem 1.5rem', cursor: 'pointer', background: subject === s ? (dark ? '#2d2d44' : '#f0f0f0') : 'transparent', borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <BookOpen size={20} /> {s}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div style={{ width: sidebarOpen ? '250px' : '60px', background: card, borderRight: `1px solid ${border}`, display: 'flex', flexDirection: 'column', transition: 'width 0.3s', flexShrink: 0 }}>
          <div style={{ padding: '1rem', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {sidebarOpen && <h6 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>SUBJECTS</h6>}
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: 'none', border: 'none', color: text, cursor: 'pointer' }}>
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {subjects.map(s => (
              <div key={s} onClick={() => setSubject(s)} style={{ padding: sidebarOpen ? '1rem 1.5rem' : '1rem 0.5rem', cursor: 'pointer', background: subject === s ? (dark ? '#2d2d44' : '#e8eaf6') : 'transparent', borderLeft: subject === s ? `3px solid ${primary}` : '3px solid transparent', color: subject === s ? primary : text, display: 'flex', alignItems: 'center', gap: '0.75rem', justifyContent: sidebarOpen ? 'flex-start' : 'center' }} title={!sidebarOpen ? s : ''}>
                <BookOpen size={20} />
                {sidebarOpen && <span>{s}</span>}
              </div>
            ))}
          </div>
        </div>

        <div style={{ flex: tab === 'papers' && paper ? '0 0 40%' : 1, background: bg, overflowY: 'auto', padding: '1.5rem' }}>
          {tab === 'syllabus' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>{subject} Syllabus</h4>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Mains', 'Advanced'].map(e => (
                    <button key={e} onClick={() => setExamType(e)} style={{ background: examType === e ? primary : (dark ? '#2d2d44' : '#e0e0e0'), color: examType === e ? '#fff' : text, border: 'none', padding: '0.5rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}>
                      JEE {e}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {syllabusData[subject]?.[examType]?.map((t, i) => (
                  <details key={i} style={{ background: card, border: `1px solid ${border}`, borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    <summary style={{ padding: '1rem 1.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '1.05rem', listStyle: 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: primary }}>▸</span> {t.topic}
                    </summary>
                    <div style={{ padding: '0 1.5rem 1.5rem', background: dark ? '#0f0f1e' : '#f9fafb' }}>
                      <ul style={{ margin: '0.5rem 0 0', padding: '0 0 0 1.5rem', lineHeight: '1.8' }}>
                        {t.modules.map((m, j) => <li key={j} style={{ marginBottom: '0.5rem', fontSize: '0.95rem' }}>{m}</li>)}
                      </ul>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {tab === 'papers' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
                <h4 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600' }}>{subject} - Previous Papers</h4>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Mains', 'Advanced'].map(e => (
                    <button key={e} onClick={() => setExamType(e)} style={{ background: examType === e ? primary : (dark ? '#2d2d44' : '#e0e0e0'), color: examType === e ? '#fff' : text, border: 'none', padding: '0.5rem 1.2rem', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '500' }}>
                      {e}
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '1rem' }}>
                {filtered.map(p => (
                  <div key={p.id} style={{ background: card, border: `1px solid ${border}`, borderRadius: '10px', padding: '1.25rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'all 0.2s', cursor: 'pointer' }} onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.1)'; }} onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)'; }}>
                    <h6 style={{ margin: '0 0 0.5rem', color: primary, fontSize: '1.3rem', fontWeight: '700' }}>{p.year}</h6>
                    <p style={{ margin: '0 0 1rem', color: muted, fontSize: '0.9rem' }}>{p.exam}</p>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button onClick={() => setPaper(p)} style={{ background: primary, color: '#fff', border: 'none', padding: '0.6rem 1rem', borderRadius: '6px', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: '500' }}>
                        <Eye size={16} /> View
                      </button>
                      <button style={{ background: 'transparent', color: primary, border: `1px solid ${primary}`, padding: '0.6rem 1rem', borderRadius: '6px', cursor: 'pointer', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.85rem', fontWeight: '500' }}>
                        <Download size={16} /> Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'analytics' && (
            <div>
              <h4 style={{ marginBottom: '1.5rem', fontSize: '1.5rem', fontWeight: '600' }}>Historical JEE Data & Trends</h4>
              <div style={{ background: card, border: `1px solid ${border}`, borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <h5 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Qualification Trends (1975-2024)</h5>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={historicalData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#333' : '#e0e0e0'} />
                    <XAxis dataKey="year" stroke={text} style={{ fontSize: '0.85rem' }} />
                    <YAxis stroke={text} style={{ fontSize: '0.85rem' }} />
                    <Tooltip contentStyle={{ background: card, border: `1px solid ${border}`, borderRadius: '8px', color: text }} />
                    <Legend wrapperStyle={{ color: text }} />
                    <Bar dataKey="mainsQualified" fill={primary} name="Mains Qualified" />
                    <Bar dataKey="advancedQualified" fill={dark ? '#ffa94d' : '#f59e0b'} name="Advanced Qualified" />
                    <Line type="monotone" dataKey="iitAdmissions" stroke={dark ? '#51cf66' : '#10b981'} strokeWidth={3} name="IIT Admissions" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              <div style={{ background: card, border: `1px solid ${border}`, borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                <h5 style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '600' }}>Syllabus Changes Over Time</h5>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={historicalData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={dark ? '#333' : '#e0e0e0'} />
                    <XAxis dataKey="year" stroke={text} style={{ fontSize: '0.85rem' }} />
                    <YAxis stroke={text} style={{ fontSize: '0.85rem' }} />
                    <Tooltip contentStyle={{ background: card, border: `1px solid ${border}`, borderRadius: '8px', color: text }} />
                    <Legend wrapperStyle={{ color: text }} />
                    <Bar dataKey="syllabusChanges" fill={dark ? '#ff6b6b' : '#ef4444'} name="Major Syllabus Changes" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1rem' }}>
                <div style={{ background: card, border: `1px solid ${border}`, borderRadius: '10px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <h6 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>
                    <TrendingUp size={20} color={dark ? '#51cf66' : '#10b981'} /> Key Statistics
                  </h6>
                  <ul style={{ margin: 0, padding: '0 0 0 1.5rem', lineHeight: '2' }}>
                    <li style={{ fontSize: '0.95rem' }}>📈 50+ years of JEE data</li>
                    <li style={{ fontSize: '0.95rem' }}>📊 280,000+ Mains qualifiers (2024)</li>
                    <li style={{ fontSize: '0.95rem' }}>🎓 17,000+ IIT seats (2024)</li>
                    <li style={{ fontSize: '0.95rem' }}>📝 Major reforms: 2013, 2000, 1990</li>
                  </ul>
                </div>
                <div style={{ background: card, border: `1px solid ${border}`, borderRadius: '10px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                  <h6 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: '600' }}>Notable Changes</h6>
                  <ul style={{ margin: 0, padding: '0 0 0 1.5rem', lineHeight: '2' }}>
                    <li style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      <strong style={{ color: primary }}>2013:</strong> IIT-JEE renamed to JEE Advanced
                    </li>
                    <li style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      <strong style={{ color: primary }}>2000:</strong> Major syllabus restructuring
                    </li>
                    <li style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      <strong style={{ color: primary }}>1990:</strong> Focus on conceptual clarity
                    </li>
                    <li style={{ fontSize: '0.9rem' }}>
                      <strong style={{ color: primary }}>2020:</strong> NTA exam conduct
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {tab === 'papers' && paper && (
          <div style={{ flex: '0 0 35%', background: card, borderLeft: `1px solid ${border}`, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '1rem 1.5rem', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h5 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>{paper.title}</h5>
              <button onClick={() => setPaper(null)} style={{ background: 'none', border: 'none', color: text, cursor: 'pointer', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', padding: '2rem', background: dark ? '#0f0f1e' : '#f9fafb' }}>
              <FileText size={64} color={muted} style={{ marginBottom: '1rem' }} />
              <p style={{ color: muted, marginBottom: '0.5rem', fontSize: '0.95rem' }}>PDF Viewer</p>
              <p style={{ color: muted, fontSize: '0.85rem', marginBottom: '1.5rem' }}>{paper.title}</p>
              <button style={{ background: primary, color: '#fff', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '500' }}>
                Open in Full Screen
              </button>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @media (max-width: 768px) {
          nav > div:first-child button { display: block !important; }
          nav > div:last-child > div:first-child button span { display: none; }
          nav > div:last-child > div:last-child span { display: none; }
          [style*="width: 250px"], [style*="width: 60px"] { display: none !important; }
        }
        details > summary { list-style: none; }
        details > summary::-webkit-details-marker { display: none; }
        details[open] > summary > span:first-child { transform: rotate(90deg); display: inline-block; transition: transform 0.2s; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: ${dark ? '#1a1a2e' : '#f1f1f1'}; }
        ::-webkit-scrollbar-thumb { background: ${primary}; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: ${dark ? '#339af0' : '#5568d3'}; }
      `}</style>
    </div>
  );
}